// Web → Blog. Hodnocení článků a schvalování komentářů z fitway40.cz.
//
// Proč jedna stránka a ne dvě: obojí odpovídá na stejnou otázku, tedy co si
// o článku čtenářky myslí. Hvězdičky říkají kolik, komentáře říkají proč.
// Rozdělené do dvou položek v menu by se to přestalo číst dohromady.
//
// Komentář se na webu neobjeví, dokud ho tady někdo nepustí. Čekající jsou
// proto nahoře a v hlavičce je vidět, kolik jich je.
//
// Data z /api/web-blog (Postgres, tabulky blog_hodnoceni a blog_komentare).
import React, { useEffect, useMemo, useState } from "react";
import {
  MessageSquare, Loader2, Star, Check, EyeOff, Trash2, Search,
  ChevronDown, ChevronUp, Send, ExternalLink, Clock, RotateCcw,
} from "lucide-react";
// fetchApi vrací rovnou rozparsované JSON a hodí výjimku na chybě.
// apiFetch vrací syrovou Response, na tohle se nehodí.
import { fetchApi } from "../../lib/api.js";
import GradientHeader from "../../components/GradientHeader.jsx";
import { Stranka, ErrorNote } from "../../components/ui.jsx";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";

const STAVY = {
  ceka:      { label: "Čeká na schválení", chip: "bg-amber-100 text-amber-700 border-amber-200",    pruh: "bg-amber-500" },
  schvaleno: { label: "Zveřejněno",        chip: "bg-emerald-100 text-emerald-700 border-emerald-200", pruh: "bg-emerald-500" },
  skryto:    { label: "Skryto",            chip: "bg-slate-100 text-slate-600 border-slate-200",    pruh: "bg-slate-400" },
};

function fmtKdy(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** Hvězdičky jen na čtení. Půlky neřešíme, průměr se dokreslí číslem vedle. */
function Hvezdy({ hodnota }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${hodnota} z 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`w-4 h-4 ${i <= Math.round(hodnota) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`}
        />
      ))}
    </span>
  );
}

export default function WebBlog() {
  const [hodnoceni, setHodnoceni] = useState([]);
  const [komentare, setKomentare] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [hledej, setHledej] = useState("");
  const [filtrStav, setFiltrStav] = useState("vse");
  const [otevreny, setOtevreny] = useState(null);
  const [odpovedi, setOdpovedi] = useState({});
  const [uklada, setUklada] = useState(null);
  const [confirmState, setConfirmState] = useState(null);

  async function nacti() {
    setErr("");
    try {
      const d = await fetchApi("/api/web-blog");
      setHodnoceni(d.hodnoceni || []);
      setKomentare(d.komentare || []);
    } catch (e) {
      setErr("Data blogu se nepodařilo načíst.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    nacti();
  }, []);

  async function vynulujHodnoceni(slug) {
    try {
      await fetchApi("/api/web-blog", {
        method: "POST",
        body: JSON.stringify({ akce: "vynulovat", slug }),
      });
      await nacti();
    } catch (e) {
      setErr("Hodnocení se nepodařilo vynulovat.");
    }
  }

  async function akce(id, akceKlic, odpoved) {
    setUklada(id);
    try {
      await fetchApi("/api/web-blog", {
        method: "POST",
        body: JSON.stringify({ id, akce: akceKlic, ...(odpoved !== undefined ? { odpoved } : {}) }),
      });
      await nacti();
    } catch (e) {
      setErr("Změnu se nepodařilo uložit.");
    } finally {
      setUklada(null);
    }
  }

  const videt = useMemo(() => {
    const q = hledej.trim().toLowerCase();
    return komentare.filter((k) => {
      if (filtrStav !== "vse" && k.stav !== filtrStav) return false;
      if (!q) return true;
      return [k.jmeno, k.text, k.slug, k.email].filter(Boolean).join(" ").toLowerCase().includes(q);
    });
  }, [komentare, hledej, filtrStav]);

  const ceka = komentare.filter((k) => k.stav === "ceka").length;

  function rozbal(id) {
    const novy = otevreny === id ? null : id;
    setOtevreny(novy);
    if (novy == null) return;
    // Po rozbalení doskrolovat, ať je vidět, co se otevřelo.
    setTimeout(() => {
      const el = document.getElementById(`komentar-${novy}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <Stranka>
      <GradientHeader
        variant="purple"
        icon={MessageSquare}
        eyebrow="Web"
        title="Blog"
        napoveda="Hodnocení článků a komentáře čtenářek."
      />

      {err && <ErrorNote>{err}</ErrorNote>}

      {/* Hodnocení článků. Bez hlasů se sekce nezobrazuje, prázdná tabulka
          jen zabírá místo a vypadá jako rozbitá stránka. */}
      {hodnoceni.length > 0 && (
        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-900">Hodnocení článků</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Hvězdičky od čtenářek. Do strukturovaných dat článku je schválně nedáváme, Google
            zakazuje hodnocení vlastního obsahu.
          </p>
          <ul className="mt-3 divide-y divide-slate-100">
            {hodnoceni.map((h) => (
              <li key={h.slug} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5">
                <a
                  href={`https://fitway40.cz/blog/${h.slug}/`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex min-w-0 flex-1 items-center gap-1.5 text-sm font-medium text-slate-900 hover:text-brand-700"
                >
                  <span className="truncate">{h.slug}</span>
                  <ExternalLink className="w-3.5 h-3.5 shrink-0 text-slate-400" />
                </a>
                <Hvezdy hodnota={Number(h.prumer)} />
                <span className="text-sm font-semibold text-slate-900">{Number(h.prumer).toFixed(1)}</span>
                <span aria-hidden className="w-px h-3 bg-slate-300" />
                <span className="text-sm text-slate-500">{h.pocet} hlasů</span>
                {h.spatne > 0 && (
                  <>
                    <span aria-hidden className="w-px h-3 bg-slate-300" />
                    <span className="text-sm text-rose-600">{h.spatne} pod tři hvězdy</span>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => setConfirmState({ slug: h.slug })}
                  title="Vynulovat hodnocení tohoto článku"
                  className="rounded-xl p-1.5 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Filtry komentářů */}
      <div className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={hledej}
            onChange={(e) => setHledej(e.target.value)}
            placeholder="Hledat v komentářích"
            className="field w-full pl-9 pr-3"
          />
        </div>
        <select
          value={filtrStav}
          onChange={(e) => setFiltrStav(e.target.value)}
          className="field w-auto"
        >
          <option value="vse">Všechny stavy</option>
          {Object.entries(STAVY).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
      </div>

      {videt.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
          {komentare.length ? "Žádný komentář neodpovídá filtru." : "Zatím nikdo nekomentoval."}
        </div>
      ) : (
        <ul className="space-y-2">
          {videt.map((k) => {
            const st = STAVY[k.stav] || STAVY.ceka;
            const open = otevreny === k.id;
            return (
              <li
                key={k.id}
                id={`komentar-${k.id}`}
                className="scroll-mt-24 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
              >
                <button
                  type="button"
                  onClick={() => rozbal(k.id)}
                  className="flex w-full items-start gap-3 p-3 text-left transition hover:bg-slate-50"
                >
                  <span aria-hidden className={`mt-0.5 h-9 w-1 shrink-0 rounded-full ${st.pruh}`} />
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="text-sm font-semibold text-slate-900">{k.jmeno}</span>
                      <span aria-hidden className="w-px h-3 bg-slate-300" />
                      <span className="text-xs text-slate-500">{k.slug}</span>
                      <span aria-hidden className="w-px h-3 bg-slate-300" />
                      <span className="text-xs text-slate-500">{fmtKdy(k.ts)}</span>
                    </span>
                    <span className="mt-1 block truncate text-sm text-slate-600">{k.text}</span>
                  </span>
                  <span className={`shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium ${st.chip}`}>
                    {st.label}
                  </span>
                  {open ? (
                    <ChevronUp className="mt-0.5 w-4 h-4 shrink-0 text-slate-400" />
                  ) : (
                    <ChevronDown className="mt-0.5 w-4 h-4 shrink-0 text-slate-400" />
                  )}
                </button>

                {open && (
                  <div className="border-t border-slate-100 p-4 space-y-4">
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-800">{k.text}</p>

                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                      {k.email && (
                        <>
                          <a href={`mailto:${k.email}`} className="text-brand-700 hover:underline">{k.email}</a>
                          <span aria-hidden className="w-px h-3 bg-slate-300" />
                        </>
                      )}
                      <a
                        href={`https://fitway40.cz/blog/${k.slug}/`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 hover:text-brand-700"
                      >
                        Otevřít článek <ExternalLink className="w-3 h-3" />
                      </a>
                      {k.odpoved_ts && (
                        <>
                          <span aria-hidden className="w-px h-3 bg-slate-300" />
                          <span className="inline-flex items-center gap-1">
                            <Clock className="w-3 h-3" /> odpovězeno {fmtKdy(k.odpoved_ts)}
                          </span>
                        </>
                      )}
                    </div>

                    {/* Odpověď se zobrazí pod komentářem na webu, podepsaná FitWay40. */}
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Odpověď pod komentářem
                      </label>
                      <textarea
                        rows={3}
                        value={odpovedi[k.id] ?? k.odpoved ?? ""}
                        onChange={(e) => setOdpovedi((o) => ({ ...o, [k.id]: e.target.value }))}
                        placeholder="Nepovinné. Zobrazí se na webu pod komentářem jako odpověď od nás."
                        className="field mt-1 w-full"
                      />
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {k.stav !== "schvaleno" && (
                        <button
                          type="button"
                          disabled={uklada === k.id}
                          onClick={() => akce(k.id, "schvalit", odpovedi[k.id] ?? k.odpoved ?? "")}
                          className="btn btn-primary"
                        >
                          <Check className="w-4 h-4" /> Zveřejnit
                        </button>
                      )}
                      {k.stav === "schvaleno" && (
                        <button
                          type="button"
                          disabled={uklada === k.id}
                          onClick={() => akce(k.id, "skryt")}
                          className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
                        >
                          <EyeOff className="w-4 h-4" /> Skrýt z webu
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={uklada === k.id}
                        onClick={() => akce(k.id, undefined, odpovedi[k.id] ?? "")}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
                      >
                        <Send className="w-4 h-4" /> Uložit odpověď
                      </button>
                      <button
                        type="button"
                        disabled={uklada === k.id}
                        onClick={() =>
                          setConfirmState({
                            id: k.id,
                            jmeno: k.jmeno,
                          })
                        }
                        className="inline-flex items-center gap-1.5 rounded-xl border border-rose-200 px-3 py-2 text-sm font-semibold text-rose-700 transition hover:bg-rose-50 disabled:opacity-50"
                      >
                        <Trash2 className="w-4 h-4" /> Smazat
                      </button>
                      {uklada === k.id && <Loader2 className="w-4 h-4 animate-spin self-center text-slate-400" />}
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <ConfirmDialog
        open={!!confirmState}
        title={confirmState?.slug ? "Vynulovat hodnocení?" : "Smazat komentář?"}
        description={
          confirmState?.slug
            ? `Všechny hvězdičky u článku ${confirmState.slug} se smažou a počítá se od nuly. Hodí se po nasazení, kdy pár hlasů vznikne při zkoušení.`
            : `Komentář od ${confirmState?.jmeno || ""} se smaže natrvalo a nepůjde vrátit. Pokud ho chcete jen stáhnout z webu, použijte Skrýt.`
        }
        confirmLabel={confirmState?.slug ? "Vynulovat" : "Smazat"}
        variant="danger"
        onConfirm={() => {
          if (confirmState.slug) vynulujHodnoceni(confirmState.slug);
          else akce(confirmState.id, "smazat");
          setConfirmState(null);
        }}
        onCancel={() => setConfirmState(null)}
      />
    </Stranka>
  );
}
